// The filter row — Brand · Country · Group · Account, matching the reporting
// dashboards' bars: the same MultiSelectFilter component (with its built-in
// type-to-filter on long lists — the Account list) and the SAME exclude-self
// faceting (reportingFacets.availableOptions) over the book's account
// dimensions. Scope picks the book; these narrow within it. Session state.

import { MultiSelectFilter, type MultiSelectOption } from '../../components/ui/MultiSelectFilter';
import { availableOptions } from '../../lib/reportingFacets';
import type { ReportingAccountFacet } from '../../types/reporting';
import type { HomeFilterSelection } from '../../lib/advertisingHome/bookFilter';

const opt = (value: string, label: string): MultiSelectOption => ({ value, label });

export function HomeFilterBar({
  facets,
  selection,
  onChange,
  amOptions,
}: {
  facets: ReportingAccountFacet[];
  selection: HomeFilterSelection;
  onChange: (next: HomeFilterSelection) => void;
  /**
   * ⚠️⚠️ THE ACCOUNT-MANAGER FILTER, AND IT IS SPECIALIST-ONLY. Advertising
   * Home already answers "whose book" with the SCOPE switcher above this bar;
   * a second AM control there would be two different questions wearing one
   * label. The specialist boards are pinned to the whole roster and have no
   * such control, which is why they get this one — and it is a filter over the
   * loaded book, never a scope change.
   *
   * Omitted (the AM board) → no control, and `accountManagerIds` stays empty,
   * i.e. no constraint. Built by `amFilterOptions`, which carries the
   * "Unassigned" entry the reporting faceting structurally cannot produce.
   */
  amOptions?: Array<{ id: string; name: string }>;
}): React.JSX.Element {
  const patch = (p: Partial<HomeFilterSelection>) => onChange({ ...selection, ...p });
  // The faceting reads the full reporting selection shape. Industry isn't a
  // filter here, and ⚠️ the AM selection is deliberately NOT fed in: its
  // "Unassigned" sentinel matches no `accountManagerId` on a facet row, so
  // cascading it would empty the other four option lists the moment somebody
  // picked it. The AM filter still narrows the BOOK (filterBookAccounts); what
  // it does not do is narrow the other filters' option lists.
  const facetSelection = { ...selection, industries: [], accountManagerIds: [] };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <MultiSelectFilter
        label="Brand"
        options={availableOptions(facets, facetSelection, 'brand').map((b) => opt(b.id, b.name))}
        selected={selection.brandAccountIds}
        onChange={(v) => patch({ brandAccountIds: v })}
      />
      <MultiSelectFilter
        label="Country"
        options={availableOptions(facets, facetSelection, 'country').map((c) => opt(c.id, c.name))}
        selected={selection.countries}
        onChange={(v) => patch({ countries: v })}
      />
      <MultiSelectFilter
        label="Group"
        options={availableOptions(facets, facetSelection, 'group').map((g) => opt(g.id, g.name))}
        selected={selection.groupAccountIds}
        onChange={(v) => patch({ groupAccountIds: v })}
      />
      <MultiSelectFilter
        label="Account"
        options={availableOptions(facets, facetSelection, 'account').map((a) => opt(a.id, a.name))}
        selected={selection.accountIds}
        onChange={(v) => patch({ accountIds: v })}
      />
      {amOptions && (
        <MultiSelectFilter
          label="Account manager"
          options={amOptions.map((a) => opt(a.id, a.name))}
          selected={selection.accountManagerIds}
          onChange={(v) => patch({ accountManagerIds: v })}
        />
      )}
    </div>
  );
}
